import 'react-pdf/dist/Page/TextLayer.css';

import { useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '@qapline/helpers';
import { config } from 'config';
import { CustomerDetailSchema } from 'modules/customers';
import { useLazyGetOneCustomerQuery } from 'modules/customers/services';
import { InvoiceBasicForm } from 'modules/form';
import { useCreateInvoiceMutation } from 'modules/invoicing/api';
import { useInvoicePdfPreview } from 'modules/invoicing/hooks';
import { InvoiceFormSchema } from 'modules/invoicing/types';
import { buildCreateInvoicePayload } from 'modules/invoicing/utils';
import { PageContent, Subheader } from 'modules/layout';
import { BackButton, Button } from 'modules/ui';

import { InvoiceDocument } from '../InvoiceDocument';

import styles from './InvoicingNewPage.module.scss';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

export const InvoicingNewPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    useDocumentTitle(t('nav.invoicing'));

    const [createInvoice, { isLoading: isSubmittingFormLoading }] = useCreateInvoiceMutation();
    const [getOneCustomer] = useLazyGetOneCustomerQuery();

    const [customer, setCustomer] = useState<CustomerDetailSchema | null>(null);

    // Nothing is saved yet, so the document on the right is always the live preview of the form.
    const [previewValues, setPreviewValues] = useState<InvoiceFormSchema | null>(null);
    const { blobUrl: previewBlobUrl, isRendering: isRenderingPreview } = useInvoicePdfPreview({
        values: previewValues,
    });

    const prepareData = async (data: InvoiceFormSchema) => {
        try {
            const body = buildCreateInvoicePayload(data);
            const response = await createInvoice(body).unwrap();
            navigate(`${config.routes.invoice.overview}/${response.invoice_id}`);
            toast.success(t('invoicing.created'));
        } catch (error) {
            toast.error(t('form.saveError'));
        }
    };

    const fetchCustomer = async (customer_id: number) => {
        const { data: customerData, isSuccess } = await getOneCustomer({ id: String(customer_id) });
        if (isSuccess) setCustomer(customerData);
    };

    return (
        <PageContent
            fullWidth
            scrollable
            subheader={
                <Subheader
                    startSlot={<BackButton />}
                    endSlot={
                        <div className={styles.actionButtons}>
                            <Button
                                isLoading={isSubmittingFormLoading}
                                disabled={isSubmittingFormLoading}
                                form="invoice-form"
                                type="submit"
                            >
                                {t('invoicing.form.save')}
                            </Button>
                        </div>
                    }
                    title={t('invoicing.newInvoice')}
                />
            }
        >
            <div className={styles.container}>
                <div className={styles.form}>
                    <InvoiceBasicForm
                        prepareData={prepareData}
                        customer={customer}
                        fetchCustomer={fetchCustomer}
                        onValuesChange={setPreviewValues}
                    />
                </div>
                <InvoiceDocument
                    blobUrl={previewBlobUrl}
                    loading={isRenderingPreview}
                    isRefreshing={isRenderingPreview}
                />
            </div>
        </PageContent>
    );
};
